/**
 * Admin GDPR Art. 17 panel — embedded in the Customer Edit modal.
 *
 * Only visible to ``super_admin`` (the user object stored in
 * localStorage). Shows three sections:
 *   1. Controversia toggle (open/close with mandatory reason)
 *   2. Manual erasure buttons (3 categories) with double-confirm dialog
 *   3. Audit log of GDPR events for THIS customer
 *
 * All actions are server-side guarded (per-category blockers in
 * services/gdpr_erasure_service.py). The UI mirrors those blockers
 * by greying out the buttons and surfacing the reason text.
 */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, FileX, MapPinOff, BookX,
  Loader2, History,
} from "lucide-react";
import { API } from "../../config";

const CATEGORY_ICONS = {
  contract: FileX,
  tracking_disputed: MapPinOff,
  compliance: BookX,
};


export const GdprAdminPanel = ({ customer, onChange }) => {
  const userRaw = localStorage.getItem("user");
  const user = userRaw ? JSON.parse(userRaw) : null;
  const isSuperAdmin = user?.role === "super_admin";

  const [eligibility, setEligibility] = useState(null);
  const [audit, setAudit] = useState([]);
  const [pending, setPending] = useState(null); // {category, reason}
  const [busy, setBusy] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token") || localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAll = async () => {
    if (!customer?.id || !isSuperAdmin) return;
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/gdpr/customers/${customer.id}/erasure/eligibility`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/gdpr/audit-log?customer_id=${customer.id}&limit=20`, { headers: getAuthHeader() }),
      ]);
      setEligibility(a.data);
      setAudit(b.data.rows || []);
    } catch (e) {
      console.warn("GDPR panel load failed", e);
    }
  };

  useEffect(() => { fetchAll(); }, [customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isSuperAdmin) return null;
  if (!eligibility) return null;

  const adminCategories = eligibility.categories.filter(c => c.layer === "admin_manual");

  const toggleDispute = async () => {
    const open = !eligibility.dispute_active;
    const reason = window.prompt(
      open
        ? "Inserisci una breve motivazione per APRIRE la controversia (min. 5 caratteri):"
        : "Inserisci una breve motivazione per CHIUDERE la controversia (facoltativa):"
    );
    if (open && (!reason || reason.length < 5)) {
      toast.error("Motivazione obbligatoria (min. 5 caratteri)");
      return;
    }
    setBusy(true);
    try {
      const url = open
        ? `${API}/admin/gdpr/customers/${customer.id}/dispute/open`
        : `${API}/admin/gdpr/customers/${customer.id}/dispute/close`;
      await axios.post(url, { reason: reason || "Chiusura controversia" }, { headers: getAuthHeader() });
      toast.success(open ? "Controversia aperta" : "Controversia chiusa");
      await fetchAll();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const startErasure = (category) => {
    setPending({ category, reason: "", confirmed: false });
  };

  const cancelErasure = () => setPending(null);

  const confirmErasure = async () => {
    if (!pending) return;
    if ((pending.reason || "").trim().length < 10) {
      toast.error("Motivazione obbligatoria (min. 10 caratteri)");
      return;
    }
    if (!pending.confirmed) {
      toast.error("Devi confermare la comprensione dell'azione irreversibile");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/gdpr/customers/${customer.id}/erasure/execute`,
        { category: pending.category, reason: pending.reason, confirm_understanding: true },
        { headers: getAuthHeader() },
      );
      toast.success(`Cancellazione "${pending.category}" eseguita`);
      setPending(null);
      await fetchAll();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 border-t border-gray-800 pt-6 space-y-5" data-testid="gdpr-admin-panel">
      <div>
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <ShieldAlert size={16} className="text-lojack-clause-400" /> GDPR Art. 17 — Pannello compliance
        </h3>
        <p className="text-gray-500 text-xs mt-1">Solo super admin. Tutte le azioni sono tracciate nell'audit log immutabile.</p>
      </div>

      {/* Dispute toggle */}
      <div className={`rounded-xl border p-4 ${eligibility.dispute_active ? "bg-red-500/5 border-red-500/40" : "bg-[#0d0d0d] border-gray-800"}`} data-testid="gdpr-dispute-block">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              {eligibility.dispute_active
                ? <><ShieldAlert size={14} className="text-red-400" /> Controversia ATTIVA</>
                : <><ShieldCheck size={14} className="text-lojack-blue-400" /> Nessuna controversia</>}
            </p>
            {eligibility.dispute_active && (
              <>
                <p className="text-gray-400 text-xs mt-1">Aperta il {eligibility.dispute_opened_at?.slice(0, 19).replace("T", " ")}</p>
                {eligibility.dispute_reason && (
                  <p className="text-lojack-clause-300 text-xs mt-1 italic">"{eligibility.dispute_reason}"</p>
                )}
              </>
            )}
          </div>
          <button
            onClick={toggleDispute}
            disabled={busy}
            className={`px-3 py-1.5 text-xs rounded-lg border ${
              eligibility.dispute_active
                ? "bg-lojack-blue-500/10 border-lojack-blue-500/40 text-lojack-blue-300 hover:bg-lojack-blue-500/20"
                : "bg-red-500/10 border-red-500/40 text-red-300 hover:bg-red-500/20"
            }`}
            data-testid="gdpr-dispute-toggle"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : eligibility.dispute_active ? "Chiudi controversia" : "Apri controversia"}
          </button>
        </div>
      </div>

      {/* Manual erasure buttons */}
      <div className="space-y-2">
        <p className="text-gray-400 text-xs uppercase tracking-wider">Cancellazione manuale</p>
        {adminCategories.map(c => {
          const Icon = CATEGORY_ICONS[c.category] || BookX;
          return (
            <div key={c.category} className={`flex items-start justify-between gap-3 rounded-xl border p-3 ${c.eligible ? "bg-[#0d0d0d] border-gray-800" : "bg-gray-700/15 border-gray-700 opacity-70"}`} data-testid={`gdpr-cat-${c.category}`}>
              <div className="flex items-start gap-2 min-w-0">
                <Icon size={14} className={c.eligible ? "text-lojack-clause-400 mt-0.5 shrink-0" : "text-gray-500 mt-0.5 shrink-0"} />
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">{c.label}</p>
                  {!c.eligible && c.blockers?.[0] && (
                    <p className="text-lojack-clause-400 text-[11px] mt-0.5">{c.blockers[0]}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => startErasure(c.category)}
                disabled={!c.eligible || busy}
                className="px-3 py-1.5 text-[11px] rounded-lg bg-red-500/10 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed border border-red-500/40 text-red-300 shrink-0"
                data-testid={`gdpr-erase-btn-${c.category}`}
              >
                Cancella…
              </button>
            </div>
          );
        })}
      </div>

      {/* Confirmation dialog */}
      {pending && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[210] flex items-center justify-center p-4" onClick={cancelErasure}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-md border border-red-500/40 p-6" onClick={e => e.stopPropagation()} data-testid="gdpr-confirm-modal">
            <h3 className="text-white font-bold text-lg mb-1">Cancellazione irreversibile</h3>
            <p className="text-gray-400 text-sm mb-4">Categoria: <strong className="text-white">{adminCategories.find(c => c.category === pending.category)?.label}</strong></p>

            <label className="block text-xs text-gray-400 mb-1">Motivazione obbligatoria (min. 10 caratteri)</label>
            <textarea
              value={pending.reason}
              onChange={e => setPending({ ...pending, reason: e.target.value })}
              className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg p-2.5 text-white text-sm focus:border-lojack-clause-500/50 focus:outline-none"
              rows={3}
              placeholder="Es: Richiesta cliente del 15/04/2027, controversia chiusa, retention scaduta…"
              data-testid="gdpr-confirm-reason"
            />

            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={pending.confirmed}
                onChange={e => setPending({ ...pending, confirmed: e.target.checked })}
                className="mt-0.5 accent-red-500"
                data-testid="gdpr-confirm-checkbox"
              />
              <span className="text-xs text-gray-300 leading-relaxed">
                Confermo di aver compreso che questa azione è <strong className="text-red-300">irreversibile</strong> e che verrà registrata permanentemente nell'audit log.
              </span>
            </label>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={cancelErasure} className="px-4 py-2 text-sm text-gray-400 hover:text-white" data-testid="gdpr-confirm-cancel">Annulla</button>
              <button
                onClick={confirmErasure}
                disabled={busy || !pending.confirmed || (pending.reason || "").length < 10}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg flex items-center gap-2"
                data-testid="gdpr-confirm-execute"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : null} Cancella ora
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit log */}
      {audit.length > 0 && (
        <div className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-3" data-testid="gdpr-audit-log">
          <p className="text-gray-400 text-xs flex items-center gap-1.5 mb-2">
            <History size={12} /> Storico eventi GDPR per questo cliente ({audit.length})
          </p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {audit.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-[11px] py-1 border-b border-gray-800/50 last:border-0">
                <span className="font-mono text-gray-500">{r.created_at?.slice(0, 19).replace("T", " ")}</span>
                <span className="text-white">{r.action}</span>
                <span className="text-gray-500 truncate max-w-[140px]">{r.actor}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GdprAdminPanel;

GdprAdminPanel.propTypes = {
  customer: PropTypes.object.isRequired,        // customer record this panel operates on
  onChange: PropTypes.func,                     // optional callback fired after erasure/state changes
};
